import type { IQueryDefaultParams } from "@/common/globalTypes/interfaces/common";
import type { PureNullable } from "@/common/globalTypes/utillityTypes/common";
import type {
  IAddApplicationReviewResponse,
  IApplicationByAliasMetaDataApiResponse,
  IApplicationByAliasMetaDataRequest,
  IApplicationByAliasResponse,
  IApplicationRateCountRequest,
  IApplicationRateCountResponse,
  IApplicationRequest,
  IApplicationReviewRequest,
  IApplicationReviewResponse,
  IApplicationReviewsResponse,
  IApplicationsByCategoryAliasApiResponse,
  IApplicationsRequest,
  IApplicationsResponse,
  IAppPermissionsRequest,
  IAppPermissionsResponse,
  IDeleteApplicationReviewResponse,
  IDeleteReviewRequest,
  IDeleteVoteRequest,
  IHelpfulAndUnhelpfulRequest,
  IMostHelpfulAndUnHelpfulReviewCountsRequest,
  IMostHelpfulAndUnHelpfulReviewCountsResponse,
  IPlanByAppIdApiResponse,
  IUpdateApplicationReviewResponse,
} from "@/services/apiHandlers/applications/types";
import { BaseApiHandler } from "@/services/apiHandlers/baseApiHandler";
import { BackendResponseModelTypes } from "@/services/http/types";

class ApplicationApiHandler extends BaseApiHandler {
  constructor() {
    super();
  }

  public async getPopularApps() {
    return this.request.applicationsFetcher.get<IApplicationsResponse[]>({
      url: "applications/populars",
      responseModel: BackendResponseModelTypes.WithData,
      next: { revalidate: 300 },
    });
  }

  public async getApplications({ ...queryParams }: IApplicationsRequest) {
    return this.request.applicationsFetcher.get<IApplicationsByCategoryAliasApiResponse>(
      {
        url: "applications",
        queryParam: { ...queryParams },
        next: { revalidate: 300 },
        responseModel: BackendResponseModelTypes.WithData,
      },
    );
  }

  public async searchApplication(
    searchTerm: string,
    queryParam: IQueryDefaultParams,
  ) {
    return this.request.applicationsFetcher.get<IApplicationsByCategoryAliasApiResponse>(
      {
        url: "search",
        queryParam: {
          searchTerm,
          ...queryParam,
        },
        responseModel: BackendResponseModelTypes.WithData,
      },
    );
  }

  public async getApplicationByAlias({
    alias,
    projectId,
  }: IApplicationRequest) {
    const queryParams = projectId && { projectId };

    return this.request.applicationsFetcher.get<IApplicationByAliasResponse>({
      url: `applications/by-alias/${alias}`,
      queryParam: { ...queryParams },
      responseModel: BackendResponseModelTypes.WithData,
    });
  }

  public async getPermissionsByAppId({ clientId }: IAppPermissionsRequest) {
    return this.request.accountsFetcher.get<IAppPermissionsResponse>({
      url: `oauth/clients/${clientId}`,
      responseModel: BackendResponseModelTypes.WithData,
    });
  }

  public async getPlanByAppId(applicationId: string, projectId: number) {
    return this.request.accountsFetcher.get<IPlanByAppIdApiResponse>({
      url: `api/applications/${applicationId}/current-plan/${projectId}`,
    });
  }

  public async getApplicationByAliasMetaDataAndJsonLd({
    alias,
  }: IApplicationByAliasMetaDataRequest) {
    return this.request.applicationsFetcher.get<IApplicationByAliasMetaDataApiResponse>(
      {
        url: `applications/by-alias/${alias}/meta-details`,
        hideErrorMessage: true,
        responseModel: BackendResponseModelTypes.WithData,
      },
    );
  }

  public async getApplicationReviews({ ...queryParams }) {
    const { applicationId, ...queryParamsRest } = queryParams;

    return this.request.applicationsFetcher.get<IApplicationReviewResponse>({
      url: `applications/${applicationId}/reviews`,
      queryParam: { ...queryParamsRest },
      responseModel: BackendResponseModelTypes.WithData,
    });
  }

  public async getApplicationRateCount({
    applicationId,
  }: IApplicationRateCountRequest) {
    return this.request.applicationsFetcher.get<
      IApplicationRateCountResponse[]
    >({
      url: `applications/${applicationId}/rate-counts`,
      responseModel: BackendResponseModelTypes.WithData,
    });
  }

  public async getMostHelpfulAndUnHelpfulReviewCounts({
    applicationId,
  }: IMostHelpfulAndUnHelpfulReviewCountsRequest) {
    return this.request.applicationsFetcher.get<
      IMostHelpfulAndUnHelpfulReviewCountsResponse[]
    >({
      url: `applications/${applicationId}/helpful-unhelpful-reviews`,
      responseModel: BackendResponseModelTypes.WithData,
    });
  }

  public async addReview({ ...review }: IApplicationReviewRequest) {
    return this.request.applicationsFetcher.post<IAddApplicationReviewResponse>(
      {
        url: `application-reviews`,
        data: review,
        responseModel: BackendResponseModelTypes.WithData,
      },
    );
  }

  public async updateReview(review: IApplicationReviewRequest) {
    return this.request.applicationsFetcher.patch<IUpdateApplicationReviewResponse>(
      {
        url: `application-reviews/${review.reviewId}`,
        data: review,
        responseModel: BackendResponseModelTypes.WithData,
      },
    );
  }

  public async deleteReview({ reviewId }: IDeleteReviewRequest) {
    return this.request.applicationsFetcher.delete<IDeleteApplicationReviewResponse>(
      {
        url: `application-reviews/${reviewId}`,
        responseModel: BackendResponseModelTypes.WithData,
      },
    );
  }

  public async helpfulOrUnhelpfulReview({
    reviewId,
    helpfulOrUnhelpful: action,
  }: IHelpfulAndUnhelpfulRequest) {
    return this.request.applicationsFetcher.post<IUpdateApplicationReviewResponse>(
      {
        url: `application-reviews/${reviewId}/vote`,
        data: {
          action,
        },
        responseModel: BackendResponseModelTypes.WithData,
      },
    );
  }

  public async deleteVoteReview({ reviewId }: IDeleteVoteRequest) {
    return this.request.applicationsFetcher.delete<IUpdateApplicationReviewResponse>(
      {
        url: `application-reviews/${reviewId}/vote`,
        responseModel: BackendResponseModelTypes.WithData,
      },
    );
  }

  public async getApplicationReviewById(reviewId: PureNullable<string>) {
    return this.request.applicationsFetcher.get<IApplicationReviewsResponse>({
      url: `application-reviews/${reviewId}`,
      responseModel: BackendResponseModelTypes.WithData,
    });
  }
}

const applicationApiHandler = new ApplicationApiHandler();

export default applicationApiHandler;
